import cf = egr.wcf.cf;
import core = egr.wcf.core;
import utils = egr.wcf.utils;
import {
    WATERMARK_RIGHT_URL, WATERMARK_LEFT_URL, WATERMARK_MARGIN_RIGHT, WATERMARK_MARGIN_BOTTOM, WATERMARK_MARGIN_LEFT,
    ProjectPreviewImageOnPhone, ProjectPreviewImage, Pica
 } from '../constants/LayoutConstants';
import { AppConfigurationStore } from '../stores/AppConfigurationStore';
import { GlobalHelper } from './GlobalHelper';
import { Converter } from './Converter';
import { FileFormat, FileExtensionMapping, FileFormats } from '../constants/FileFormat';
import { PconLoginStore, PconLoginOrganization } from '../stores/PconLoginStore';
import { UIHelper } from './UIHelper';
import { PersistentStore } from '../stores/PersistentStore';
import { ProjectSettingsStore, GroundType, FloorPlanSettings } from '../stores/ProjectSettingsStore';

interface ScreenshotHelperOptions extends core.view.ScreenshotOptions {
    /**
     * Remove the url scheme and mime type info (default: false)
     */
    stripUrlScheme?: boolean;

    /**
     * Keep the scene ration (default: true)
     */
    adjustSize?: boolean;

    /**
     * This option specifies whether a watermark should be added to the
     * image or not (default: false)
     */
    addWatermark?: boolean;
    customMimetype?: boolean;
}

export abstract class ScreenshotHelper {
    public static readonly initialize: null = ((): null => {
        // Ensure that the font is loaded
        setTimeout(
            (): void => {
                ScreenshotHelper.createWatermark('.');
            },
            1000
        );

        return null;
    })();

    private static bottomLeftWatermark: HTMLImageElement | null = null;
    private static bottomRightWatermark: HTMLImageElement | null = null;

    public static getWatermarkText(): string {
        const organization: PconLoginOrganization | null = PconLoginStore.getState().organization;
        if (organization != null && !utils.string.isNullOrEmpty(organization.name)) {
            return organization.name;
        }

        return 'powered by EasternGraphics GmbH';
    }

    public static screenshotAllowed(): boolean {
        if (
            true &&
            AppConfigurationStore.getState().iDevice &&
            (window.navigator as any).standalone === true
        ) {
            return false;
        }
        return true;
    }

    public static async getProjectPreviewImage(onPhone: boolean): Promise<string> {
        const width: number = (onPhone ? ProjectPreviewImageOnPhone.width : ProjectPreviewImage.width);
        const height: number = (onPhone ? ProjectPreviewImageOnPhone.height : ProjectPreviewImage.height);
        const floorPlan: FloorPlanSettings | null = ProjectSettingsStore.getState().floorPlan;
        if (floorPlan == null || !floorPlan.visible) {
            return await GlobalHelper.coreApp.viewer.createScreenshot(
                GlobalHelper.activeView,
                {
                    width: width,
                    height: height,
                    antiAliasingQuality: 4,
                    adjustSize: false
                } as core.view.ScreenshotOptions
            );
        // We need to increase image quality when floorplan is visible, else the floorplan may not be visible in result
        }

        const ratio: number = width / height;
        let sourceHeight: number = GlobalHelper.activeView.height;
        let sourceWidth: number = GlobalHelper.activeView.height * ratio;
        // Decrease size of source-image to max. 600x600 pixel
        if (sourceWidth > 600) {
            sourceHeight *= 600 / sourceWidth;
            sourceWidth = 600;
        }
        if (sourceHeight > 600) {
            sourceWidth *= 600 / sourceHeight;
            sourceHeight = 600;
        }
        const basicScreenshot: string = await GlobalHelper.coreApp.viewer.createScreenshot(
            GlobalHelper.activeView,
            {
                width: sourceWidth,
                height: sourceHeight,
                antiAliasingQuality: 4,
                adjustSize: false
            } as core.view.ScreenshotOptions
        );
        const tmpCanvas: HTMLCanvasElement = document.createElement('canvas');
        tmpCanvas.width = width;
        tmpCanvas.height = height;
        return await new Promise<string>((resolve, reject) => {
            const tmpImage: HTMLImageElement = document.createElement('img');
            tmpImage.onload = () => {
                Pica.resize(
                    tmpImage,
                    tmpCanvas,
                    {cancelToken: null}
                ).then((resultCanvas: any) => { resolve(tmpCanvas.toDataURL()); });
            };
            tmpImage.src = basicScreenshot;
        });
    }

    public static async getScreenshot(options: ScreenshotHelperOptions): Promise<string> {
        if (options.adjustSize !== false) {
            const ratio: number = Math.min(
                options.width / GlobalHelper.activeView.width,
                options.height / GlobalHelper.activeView.height
            );

            options = jQuery.extend({}, options, {
                width: Math.round(GlobalHelper.activeView.width * ratio),
                height: Math.round(GlobalHelper.activeView.height * ratio)
            }) as ScreenshotHelperOptions;
        }
        if (options.customMimetype) {
            options = jQuery.extend({}, options, ScreenshotHelper.imageOptions()) as ScreenshotHelperOptions;
        }

        // do not hide floorplan even if the image-setting is png + transparent
        if (PersistentStore.getState().imageFormat === 'PNG*') {
            options = jQuery.extend({}, options, {
                onBeforeRender: (): void => {
                    GlobalHelper.grid.setEnabled(false);
                    GlobalHelper.floorPlane.setEnabled(false);
                },
                onAfterRender: (): void => {
                    if (ProjectSettingsStore.getFloorPlanVisibility()) {
                        GlobalHelper.floorPlan.enabled = true;
                    } else {
                        const value: GroundType = ProjectSettingsStore.getState().groundType || 'solid';
                        GlobalHelper.grid.setEnabled(value === 'grid');
                        GlobalHelper.floorPlane.setEnabled(value === 'solid');
                    }
                }
            }) as ScreenshotHelperOptions;
        }

        let screenshot: string | null = await GlobalHelper.coreApp.viewer.createScreenshot(
            GlobalHelper.activeView,
            options
        );

        if (options.addWatermark === true) {
            const screenWithWatermark: string | null = await ScreenshotHelper.addWatermark(screenshot, options);
            if (utils.string.isNotNullOrEmpty(screenWithWatermark)) {
                screenshot = screenWithWatermark;
            }
        }

        if (options.stripUrlScheme === true) {
            return Converter.dataUrlToBase64String(screenshot);
        }

        return screenshot;
    }

    public static async getArticleImage(article: cf.ArticleElement, options: cf.ArticleScreenshotOptions): Promise<string | null> {
        if (article instanceof cf.MainArticleElement && article.hasPlaceholderGeometry === true) {
            // Note: the requested image size is ignore (which works for now -> should be fixed
            // later on (if it causes problems))
            const image: cf.ArticleCatalogImage | null = await article.getCatalogImage();
            if (image == null) {
                return null;
            }

            // Note: we have to use a datauri instead of a bloburi in order
            // to use the image for the pdf export
            return utils.arraybuffer.toDataUri(
                image.data,
                ScreenshotHelper.getMimetype(image.name)
            );
        }

        return await GlobalHelper.articleManager.createArticleScreenshot(article, options);
    }

    private static async loadWatermarks(): Promise<void> {
        if (
            (WATERMARK_RIGHT_URL != null || WATERMARK_LEFT_URL != null) &&
            (
                ScreenshotHelper.bottomRightWatermark == null &&
                ScreenshotHelper.bottomLeftWatermark == null
            )
        ) {
            [ScreenshotHelper.bottomRightWatermark, ScreenshotHelper.bottomLeftWatermark] = await Promise.all<HTMLImageElement | null>([
                UIHelper.loadImage(WATERMARK_RIGHT_URL),
                UIHelper.loadImage(WATERMARK_LEFT_URL)
            ]);
        }
    }

    private static createWatermark(text: string, baseCanvas: HTMLCanvasElement | null = null): Promise<string | null> {
        const padding: number = 1;
        const height: number = Math.ceil(11 * 1.25);
        const baseline: number = 11;
        const font: string = '400 11px \'Open Sans\', Tahoma, \'Geneva\', sans-serif';
        const fillStyle: string  = '#f4f4f4';

        const canvas: HTMLCanvasElement = document.createElement('canvas');
        const context: CanvasRenderingContext2D | null = canvas.getContext('2d');

        if (context == null) {
            return Promise.resolve(null);
        }

        // set font before measuring
        context.font = font;

        canvas.width = Math.ceil(context.measureText(text).width) + (2 * padding);
        canvas.height = height + (2 * padding);
        // Note: we have to reapply the `font` after the canvas was resized
        // (`save` and `restore` do not work)
        context.font = font;
        context.fillStyle = fillStyle;

        if (baseCanvas != null) {
            const brightness: number = ScreenshotHelper.getBrightness(
                baseCanvas,
                baseCanvas.width - canvas.width - WATERMARK_MARGIN_RIGHT,
                baseCanvas.height - canvas.height - WATERMARK_MARGIN_BOTTOM,
                canvas.width,
                canvas.height,
            );
            if (brightness < 75) {
                // dark background
                context.fillStyle = '#ffffff';
            } else if (brightness > 220) {
                // light backgroundColor
                context.fillStyle = '#000000';
            }
        }

        context.fillText(text, padding, baseline + padding);

        return ScreenshotHelper.canvasToUrl(canvas);
    }

    private static async getLoginWatermark(baseCanvas: HTMLCanvasElement): Promise<HTMLImageElement | null> {
        // INFO: Cache deactivated because text-color will change depending on current brightness behind the text
        const text: string = ScreenshotHelper.getWatermarkText();
        const watermarkUrl: string | null = await ScreenshotHelper.createWatermark(text, baseCanvas);
        return UIHelper.loadImage(watermarkUrl);
    }

    private static getBrightness(
        canvas: HTMLCanvasElement,
        x: number, y: number,
        width: number, height: number
    ): number {
        let colorSum: number = 0;
        const ctx: CanvasRenderingContext2D | null = canvas.getContext('2d');
        if (ctx == null) {
            return 0;
        }
        const imageData: ImageData = ctx.getImageData(x, y, width, height);
        const data: Uint8ClampedArray = imageData.data;
        let r: number;
        let g: number;
        let b: number;
        let avg: number;

        for (let pixel: number = 0; pixel < data.length; pixel += 4) {
            r = data[pixel];
            g = data[pixel + 1];
            b = data[pixel + 2];
            avg = Math.floor((r + g + b) / 3);
            colorSum += avg;
        }
        return Math.floor(colorSum / (width * height));
    }

    private static async addWatermark(data: string, options: ScreenshotHelperOptions): Promise<string | null> {
        await ScreenshotHelper.loadWatermarks();

        const img: HTMLImageElement | null = await UIHelper.loadImage(data);
        if (img == null) {
            return null;
        }
        const canvas: HTMLCanvasElement = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx: CanvasRenderingContext2D | null = canvas.getContext('2d');
        if (ctx == null) {
            return null;
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        if (ScreenshotHelper.bottomRightWatermark != null) {
            if (PconLoginStore.isLoggedIn()) {
                const loginImage: HTMLImageElement | null = await ScreenshotHelper.getLoginWatermark(canvas);
                if (loginImage == null) {
                    return null;
                }
                ctx.drawImage(
                    loginImage,
                    canvas.width - loginImage.width - WATERMARK_MARGIN_RIGHT,
                    canvas.height - loginImage.height - WATERMARK_MARGIN_BOTTOM,
                    loginImage.width,
                    loginImage.height
                );
            } else {
                ctx.drawImage(
                    ScreenshotHelper.bottomRightWatermark,
                    canvas.width - ScreenshotHelper.bottomRightWatermark.width - WATERMARK_MARGIN_RIGHT,
                    canvas.height - ScreenshotHelper.bottomRightWatermark.height - WATERMARK_MARGIN_BOTTOM,
                    ScreenshotHelper.bottomRightWatermark.width,
                    ScreenshotHelper.bottomRightWatermark.height
                );
            }
        }

        if (ScreenshotHelper.bottomLeftWatermark != null) {
            ctx.drawImage(
                ScreenshotHelper.bottomLeftWatermark,
                WATERMARK_MARGIN_LEFT,
                canvas.height - ScreenshotHelper.bottomLeftWatermark.height - WATERMARK_MARGIN_BOTTOM,
                ScreenshotHelper.bottomLeftWatermark.width,
                ScreenshotHelper.bottomLeftWatermark.height
            );
        }

        return canvas.toDataURL(options.mimeType != null ? options.mimeType : undefined);
    }

    private static canvasToUrl(canvas: HTMLCanvasElement, mimeType?: string): Promise<string> {
        return new Promise<string>((resolve: (url: string) => void, reject: (error: Error) => void): void => {
            if (canvas.toBlob != null) {
                canvas.toBlob((data: Blob | null): void => {
                    resolve(URL.createObjectURL(data));
                });
            } else {
                resolve(canvas.toDataURL(mimeType));
            }
        });
    }

    private static imageOptions = (): {mimeType?: string, backgroundColor?: BABYLON.Color4} => {
        let result: {} = {};
        if (PersistentStore.getState().imageFormat === 'JPG') {
            result = jQuery.extend({}, result, {mimeType: 'image/jpeg'});
        }

        if (PersistentStore.getState().imageFormat === 'PNG*') {
            result = jQuery.extend({}, result, {backgroundColor: new BABYLON.Color4(0, 0, 0, 0)});
        }
        return result;
    };

    private static getMimetype(filename: string): string {
        const index: number = filename.lastIndexOf('.');
        if (index !== -1) {
            filename = filename.substr(index);
        }

        filename = filename.toLocaleLowerCase();

        const format: FileFormat = FileExtensionMapping[filename];
        if (format == null) {
            return 'application/octet-stream';
        }

        return FileFormats[format].mimetype;
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/ScreenshotHelper.ts